import React, { useEffect, useState, useRef } from "react";
import PageShell from "./PageShell";
import "./PortalUI.css";
import {
    getClients,
    createClient,
    updateClient,
    deleteClient,
} from "./api";

function Clients({ onLogout, embedded = false }) {
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const [search, setSearch] = useState("");

    // Modal state
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalError, setModalError] = useState("");
    const [saving, setSaving] = useState(false);

    const emptyForm = {
        id: null,
        name: "",
        contact_name: "",
        contact_email: "",
        contact_phone: "",
        address: "",
        signup_date: "",
        referred_by_client_id: "",
        vat_number: "",
        notes: "",
        payment_method: "",
    };

    const [form, setForm] = useState(emptyForm);

    // Snapshot of form when modal is opened (for unsaved-changes check)
    const [initialFormSnapshot, setInitialFormSnapshot] = useState(emptyForm);

    // Ref to the modal form so we can trigger submit from keyboard shortcut
    const formRef = useRef(null);

    // Helper: detect unsaved changes
    const hasUnsavedChanges =
        JSON.stringify(form) !== JSON.stringify(initialFormSnapshot);

    // Load clients
    useEffect(() => {
        async function load() {
            try {
                const list = await getClients();
                setClients(list);
            } catch (err) {
                console.error(err);
                setError(err.message || "Failed to load clients");
            } finally {
                setLoading(false);
            }
        }
        load();
    }, []);

    // Core close (no checks)
    const closeModal = () => {
        setIsModalOpen(false);
        setModalError("");
        setForm(emptyForm);
        setInitialFormSnapshot(emptyForm);
    };

    // Guarded close: asks about unsaved changes
    const requestCloseModal = () => {
        if (hasUnsavedChanges) {
            const ok = window.confirm(
                "You have unsaved changes. Close without saving?"
            );
            if (!ok) return;
        }
        closeModal();
    };

    // Open modal for create
    const handleOpenCreate = () => {
        const fresh = { ...emptyForm };
        setForm(fresh);
        setInitialFormSnapshot(fresh);
        setModalError("");
        setIsModalOpen(true);
    };

    // Open modal for edit
    const handleOpenEdit = (c) => {
        setModalError("");
        const nextForm = {
            id: c.id,
            name: c.name || "",
            contact_name: c.contact_name || "",
            contact_email: c.contact_email || "",
            contact_phone: c.contact_phone || "",
            address: c.address || "",
            signup_date: c.signup_date || "",
            referred_by_client_id: c.referred_by_client_id || "",
            vat_number: c.vat_number || "",
            notes: c.notes || "",
            payment_method: c.payment_method || "",
        };
        setForm(nextForm);
        setInitialFormSnapshot(nextForm);
        setIsModalOpen(true);
    };

    // Save (create or update)
    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        setModalError("");

        try {
            const payload = {
                name: form.name,
                contact_name: form.contact_name || null,
                contact_email: form.contact_email || null,
                contact_phone: form.contact_phone || null,
                address: form.address || null,
                signup_date: form.signup_date || null,
                referred_by_client_id: form.referred_by_client_id || null,
                vat_number: form.vat_number || null,
                notes: form.notes || null,
                payment_method: form.payment_method || null,
            };

            let saved;

            if (form.id) {
                saved = await updateClient(form.id, payload);
                setClients((prev) =>
                    prev.map((c) => (c.id === saved.id ? saved : c))
                );
            } else {
                saved = await createClient(payload);
                setClients((prev) =>
                    [...prev, saved].sort((a, b) =>
                        a.name.localeCompare(b.name)
                    )
                );
            }

            closeModal();
        } catch (err) {
            console.error(err);
            setModalError(err.message || "Failed to save client");
        } finally {
            setSaving(false);
        }
    };

    // Delete client
    const handleDelete = async () => {
        if (!form.id) return;
        if (!window.confirm("Delete this client?")) return;

        try {
            await deleteClient(form.id);
            setClients((prev) => prev.filter((c) => c.id !== form.id));
            closeModal();
        } catch (err) {
            console.error(err);
            setModalError(err.message || "Failed to delete client");
        }
    };

    // Keyboard shortcuts: ESC to close (with unsaved warning), Ctrl/Cmd+S to save
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (!isModalOpen) return;

            // ESC = request close with unsaved check
            if (e.key === "Escape") {
                e.preventDefault();
                requestCloseModal();
                return;
            }

            // Ctrl+S / Cmd+S = save
            const isSaveShortcut =
                (e.key === "s" || e.key === "S") &&
                (e.ctrlKey || e.metaKey);

            if (isSaveShortcut) {
                e.preventDefault();
                if (!saving && formRef.current) {
                    // Trigger native form submit -> React calls handleSave
                    if (formRef.current.requestSubmit) {
                        formRef.current.requestSubmit();
                    } else {
                        formRef.current.submit();
                    }
                }
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [isModalOpen, saving, requestCloseModal, formRef, hasUnsavedChanges]);

    // Filtered list
    const visibleClients = [...clients].filter((c) =>
        c.name.toLowerCase().includes(search.toLowerCase())
    );

    return (
        <PageShell embedded={embedded} onLogout={onLogout}>
            <div className="portal-page">
                {/* Page Header */}
                <div className="portal-page__header">
                    <div>
                        <h2 className="portal-page__title">Clients</h2>
                        <p className="portal-page__subtitle">
                            View, search and manage client accounts.
                        </p>
                    </div>

                    <button className="btn btn--primary" onClick={handleOpenCreate}>
                        + Create Client
                    </button>
                </div>

                {/* Search Bar */}
                <div className="portal-filter-bar">
                    <div className="portal-filter-bar__group portal-filter-bar__group--grow">
                        <span className="portal-filter-bar__label">Search</span>
                        <input
                            type="text"
                            className="portal-filter-bar__search"
                            placeholder="Search clients…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                </div>

                {/* Error */}
                {error && <div className="alert alert--error">{error}</div>}

                {/* Client Cards */}
                {loading && <p>Loading clients…</p>}

                {!loading && visibleClients.length === 0 && (
                    <p>No clients found.</p>
                )}

                {!loading && visibleClients.length > 0 && (
                    <div className="episode-list">
                        {visibleClients.map((c) => (
                            <div
                                key={c.id}
                                className="episode-card"
                                style={{ borderLeftColor: "#1d3557" }}
                                onClick={() => handleOpenEdit(c)}
                            >
                                <div className="episode-card__main">
                                    <h3 className="episode-card__title">{c.name}</h3>
                                    <div className="episode-card__podcast">
                                        {c.contact_name || "No contact name"}
                                    </div>
                                    <div className="episode-card__meta">
                                        {c.contact_email || "No email"} •{" "}
                                        {c.contact_phone || "No phone"}
                                    </div>
                                </div>

                                <div className="episode-card__right">
                                    <span className="pill pill--role-client">
                                        Client
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* ---------------- MODAL ---------------- */}
            {isModalOpen && (
                <div className="modal-overlay">
                    {/* 🔹 Use normal-width modal here (no modal--wide) */}
                    <div className="modal">
                        {/* Header */}
                        <div className="modal__header">
                            <div>
                                <h3 className="modal__title">
                                    {form.id ? "Edit Client" : "Create Client"}
                                </h3>
                                <p className="modal__subtitle">
                                    Manage client details and account information.
                                </p>
                            </div>

                            <button
                                className="modal__close-btn"
                                onClick={requestCloseModal}
                            >
                                ✕
                            </button>
                        </div>

                        {modalError && (
                            <div className="alert alert--error">{modalError}</div>
                        )}

                        {/* Body */}
                        <form
                            className="modal__body"
                            onSubmit={handleSave}
                            ref={formRef}
                        >
                            {/* 🔹 Stack sections instead of forcing two columns */}
                            <div className="modal__grid">
                                {/* Left "column" (first section) */}
                                <div className="modal__column">
                                    <fieldset className="fieldset">
                                        <legend className="fieldset__legend">Basic Info</legend>

                                        <label className="form-field">
                                            <span className="form-field__label">Client Name</span>
                                            <input
                                                name="name"
                                                value={form.name}
                                                onChange={(e) =>
                                                    setForm({ ...form, name: e.target.value })
                                                }
                                                required
                                                className="form-field__input"
                                            />
                                        </label>

                                        <label className="form-field">
                                            <span className="form-field__label">
                                                Contact Name
                                            </span>
                                            <input
                                                name="contact_name"
                                                value={form.contact_name}
                                                onChange={(e) =>
                                                    setForm({
                                                        ...form,
                                                        contact_name: e.target.value,
                                                    })
                                                }
                                                className="form-field__input"
                                            />
                                        </label>

                                        <label className="form-field">
                                            <span className="form-field__label">Email</span>
                                            <input
                                                type="email"
                                                name="contact_email"
                                                value={form.contact_email}
                                                onChange={(e) =>
                                                    setForm({
                                                        ...form,
                                                        contact_email: e.target.value,
                                                    })
                                                }
                                                className="form-field__input"
                                            />
                                        </label>

                                        <label className="form-field">
                                            <span className="form-field__label">Phone</span>
                                            <input
                                                name="contact_phone"
                                                value={form.contact_phone}
                                                onChange={(e) =>
                                                    setForm({
                                                        ...form,
                                                        contact_phone: e.target.value,
                                                    })
                                                }
                                                className="form-field__input"
                                            />
                                        </label>
                                    </fieldset>

                                    <fieldset className="fieldset">
                                        <legend className="fieldset__legend">
                                            Business Details
                                        </legend>

                                        <label className="form-field">
                                            <span className="form-field__label">VAT Number</span>
                                            <input
                                                name="vat_number"
                                                value={form.vat_number}
                                                onChange={(e) =>
                                                    setForm({
                                                        ...form,
                                                        vat_number: e.target.value,
                                                    })
                                                }
                                                className="form-field__input"
                                            />
                                        </label>

                                        <label className="form-field">
                                            <span className="form-field__label">
                                                Payment Method
                                            </span>
                                            <input
                                                name="payment_method"
                                                value={form.payment_method}
                                                onChange={(e) =>
                                                    setForm({
                                                        ...form,
                                                        payment_method: e.target.value,
                                                    })
                                                }
                                                placeholder="Direct Debit, Bank Transfer…"
                                                className="form-field__input"
                                            />
                                        </label>
                                    </fieldset>
                                </div>

                                {/* Right "column" (second section, stacked below) */}
                                <div className="modal__column">
                                    <fieldset className="fieldset">
                                        <legend className="fieldset__legend">
                                            Additional Info
                                        </legend>

                                        <label className="form-field">
                                            <span className="form-field__label">Address</span>
                                            <textarea
                                                name="address"
                                                value={form.address}
                                                onChange={(e) =>
                                                    setForm({
                                                        ...form,
                                                        address: e.target.value,
                                                    })
                                                }
                                                className="form-field__textarea"
                                                rows={3}
                                            />
                                        </label>

                                        <label className="form-field">
                                            <span className="form-field__label">
                                                Sign Up Date
                                            </span>
                                            <input
                                                type="date"
                                                name="signup_date"
                                                value={form.signup_date || ""}
                                                onChange={(e) =>
                                                    setForm({
                                                        ...form,
                                                        signup_date: e.target.value,
                                                    })
                                                }
                                                className="form-field__input"
                                            />
                                        </label>

                                        <label className="form-field">
                                            <span className="form-field__label">
                                                Referred By
                                            </span>
                                            <select
                                                name="referred_by_client_id"
                                                value={form.referred_by_client_id || ""}
                                                onChange={(e) =>
                                                    setForm({
                                                        ...form,
                                                        referred_by_client_id: e.target.value,
                                                    })
                                                }
                                                className="form-field__select"
                                            >
                                                <option value="">— None —</option>
                                                {clients
                                                    .filter((c) => c.id !== form.id)
                                                    .map((c) => (
                                                        <option key={c.id} value={c.id}>
                                                            {c.name}
                                                        </option>
                                                    ))}
                                            </select>
                                        </label>

                                        <label className="form-field">
                                            <span className="form-field__label">Notes</span>
                                            <textarea
                                                name="notes"
                                                value={form.notes}
                                                onChange={(e) =>
                                                    setForm({
                                                        ...form,
                                                        notes: e.target.value,
                                                    })
                                                }
                                                className="form-field__textarea"
                                                rows={3}
                                            />
                                        </label>
                                    </fieldset>
                                </div>
                            </div>

                            {/* Footer */}
                            <div className="modal__footer">
                                {form.id && (
                                    <button
                                        type="button"
                                        className="btn btn--danger"
                                        onClick={handleDelete}
                                    >
                                        Delete
                                    </button>
                                )}

                                <div style={{ display: "flex", gap: "10px" }}>
                                    <button
                                        type="button"
                                        className="btn btn--secondary"
                                        onClick={requestCloseModal}
                                    >
                                        Cancel
                                    </button>

                                    <button
                                        type="submit"
                                        disabled={saving}
                                        className="btn btn--primary"
                                    >
                                        {saving ? "Saving..." : "Save Client"}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </PageShell>
    );
}

export default Clients;
